import { db, getEnvDbPrefix } from '@/core/firebase/config';
import type { Office } from '@/features/office/types';
import { get, onValue, ref } from 'firebase/database';
import { useEffect, useState } from 'react';
import { naturalSort } from '@/shared/utils/sort';

export async function fetchOfficeList(): Promise<Office[]> {
  const dbStringRef = getEnvDbPrefix('office');
  const snapshot = await get(ref(db, dbStringRef));
  if (!snapshot.exists()) {
    return [];
  }
  const data = snapshot.val() as Record<string, Office>;
  const offices = Object.entries(data).map(
    ([id, officeData]) =>
      ({
        ...officeData,
        id,
      }) as Office
  );
  offices.sort((a, b) => naturalSort(a.name, b.name));
  return offices;
}

type OfficeListener = (offices: Office[]) => void;

let cachedOffices: Office[] = [];
let hasCachedOffices = false;
const officeListeners = new Set<OfficeListener>();
let stopOfficeListener: (() => void) | null = null;
let officeListenerPath: string | null = null;

function officesFromSnapshot(data: Record<string, Office> | null): Office[] {
  const offices = data
    ? Object.entries(data).map(
        ([id, officeData]) =>
          ({
            ...officeData,
            id,
          }) as Office
      )
    : [];
  offices.sort((a, b) => naturalSort(a.name, b.name));
  return offices;
}

function subscribeToOfficeList(path: string, listener: OfficeListener) {
  if (officeListenerPath !== path) {
    stopOfficeListener?.();
    stopOfficeListener = null;
    cachedOffices = [];
    hasCachedOffices = false;
    officeListenerPath = path;
  }

  officeListeners.add(listener);
  if (hasCachedOffices) listener(cachedOffices);

  if (!stopOfficeListener) {
    stopOfficeListener = onValue(
      ref(db, path),
      snapshot => {
        cachedOffices = officesFromSnapshot(snapshot.val() as Record<string, Office> | null);
        hasCachedOffices = true;
        officeListeners.forEach(notify => notify(cachedOffices));
      },
      errorObject => {
        console.error('The read failed: ' + errorObject);
      }
    );
  }

  return () => {
    officeListeners.delete(listener);
    if (officeListeners.size === 0 && stopOfficeListener) {
      stopOfficeListener();
      stopOfficeListener = null;
    }
  };
}

export default function useGetOfficeList(enabled = true) {
  const [offices, setOffices] = useState<Office[]>(
    enabled && hasCachedOffices ? cachedOffices : []
  );
  const dbStringRef = getEnvDbPrefix('office');

  useEffect(() => {
    if (!enabled) {
      setOffices([]);
      return;
    }
    return subscribeToOfficeList(dbStringRef, setOffices);
  }, [dbStringRef, enabled]);

  return offices;
}
